import * as SalesStore from '../SalesStore';
import { ActionButtonDefinitionFactory } from 'pos/ui';
import { ProductForSaleDTO, FlowAction, ActionButtonDefinitionDTO, Res, TicketLineDTO, Permission, SizeDTO, ColorDTO, WhenPrint, SearchMode } from 'core/api';
import { Command } from 'pos/common';
import { SaleModuleNextAction } from 'pos/modules/helpers/SaleModuleNextAction';

import { Message } from 'core/ui-next/modal';
import { CustomerDisplay, UserActivityMonitor } from 'pos/services';
import { SessionProvider } from 'core/infrastructure';
import autoPrepareIfNeeded from '../utils/autoPrepareIfNeeded';


type Params = {
	hasCashControlNotification: () => boolean,
	hasTakeOutOrdersNotification: () => boolean,
	applyFlowAction: (action: FlowAction) => Promise<void>,
	clearIntroducedText: () => void,
	getIntroducedText: () => string;
	getIntroducedQuantity: () => number | undefined;
	getSelectedLine: () => Nullable<TicketLineDTO>,
	getSearchMode: () => SearchMode,
	getPrintMode: () => WhenPrint,
	onReloadPrices: () => Promise<void>,
	onProductSelected: (product: ProductForSaleDTO, quantity?: number, size?: SizeDTO, color?: ColorDTO) => Promise<Result>,
	onPrintModeChanged: (mode: WhenPrint) => void
	onToggleSearchMode: () => void,
};

export function createSaleScreenActions(params: Params): ActionButtonDefinitionFactory.SaleScreenActionCommands {

	const updateTicketAndWarnOnPendingAmountChanged = async () => {
		const currentPendingAmount = SalesStore.getTicket().PendingAmount;
		await SalesStore.updateTicket();
		const newPendingAmount = SalesStore.getTicket().PendingAmount;
		if (newPendingAmount === currentPendingAmount)
			return true;

		await Message.show(Res.Msg.MSG_TICKET_PENDING_AMOUNT_HAS_CHANGED_REVIEW_CHANGES_BEFORE_CONTINUE());
		return false;
	};

	const doCloseDay = async () => {
		await SalesStore.release();
		const result = await Command.execute('closeDay');
		if (result.ok) {
			await params.applyFlowAction(FlowAction.ChangeUser);
			return;
		}
		// Si no me lo han cerrado trato de cargarlo, de lo contrario esto carga uno nuevo
		await SalesStore.restorePreviousTicket();
	};

	const releaseAndLoadTicket = async (fn: () => Promise<Result<SaleModuleNextAction>>) => {

		await SalesStore.release();
		const result = await fn();

		if (!result.ok) {
			await SalesStore.restorePreviousTicket();
			return;
		}

		switch (result.value.type) {
			case 'loadTicket':
				await SalesStore.loadTicketById(result.value.ticketId);
				break;
		}
	};

	const closeOrRefundCurrentTicket = async () => {

		const closeAs = SalesStore.ticketCanBeClosedAsRefund() ? 'Refund' : 'Invoice';

		if (closeAs === 'Refund') {
			const ok = (await Message.confirm(Res.Msg.MSG_EMIT_REFUND())).ok;
			if (!ok)
				return;
		}

		await Command.execute('closeCurrentTicket', { closeAs });
		await params.onReloadPrices();
	};

	const getIntroducedValue = () => {
		const introducedQuantity = params.getIntroducedQuantity();
		return introducedQuantity === undefined ? null : introducedQuantity;
	};

	return {

		executeDirectAction: async (action: ActionButtonDefinitionDTO & { imageSrc: string }) => {

			await UserActivityMonitor.registerWhile(async () => {

				if (!await updateTicketAndWarnOnPendingAmountChanged())
					return;

				const selectedLine = params.getSelectedLine();
				const selectedLineIndex = SalesStore.getTicket().Lines.findIndex(x => x.Id === selectedLine?.Id);

				const result = await Command.execute('runDirectAction', {
					actionId: action.ActionId,
					buttonText: action.Text,
					introducedValue: getIntroducedValue(),
					selectedLine: selectedLine,
					imageSrc: action.imageSrc,
					externalActionArgs: {
						posId: SessionProvider.getCurrent().PosId,
						userId: SessionProvider.getCurrent().UserId,
						ticketId: SalesStore.getTicket().Id,
						ticketGlobalId: SalesStore.getTicket().GlobalId,
						ticketLineIndex: selectedLineIndex >= 0 ? selectedLineIndex : null
					}
				});

				params.clearIntroducedText();

				if (result.ok) {
					switch (result.value) {
						case 'none':
							break;
						case 'updateTicket':
							await SalesStore.updateTicket();
							break;
						case 'afterClose':
							await SalesStore.createNewOrGetUnusedTicket();
							await params.applyFlowAction(SessionProvider.getAfterCloseTicketAction(SalesStore.getLocation()));
							break;
						case 'afterOpenCashdrawer':
							await params.applyFlowAction(SessionProvider.getCurrent().AfterOpenCashdrawerAction);
							break;
						case 'afterPrepare':
							await params.applyFlowAction(SessionProvider.getCurrent().AfterPrepareTicketAction);
							break;
						case 'afterPrint':
							await params.applyFlowAction(SessionProvider.getCurrent().AfterPrintTicketAction);
							break;
					}
				}
			});
		},
		setProductAvailability: async () => {
			await Command.execute('setProductAvailability');
		},
		registerInputCashOperation: async () => {
			await Command.execute('registerInputCahsOperation');
		},
		registerOutputCashOperation: async () => {
			await Command.execute('registerOutputCashOperation');
		},
		openCashDrawer: async () => {
			const result = await Command.execute('openCashDrawer');
			if (result.ok)
				await params.applyFlowAction(result.value);
		},
		printPartialBalance: async () => {
			await Command.execute('printPartialBalance');
		},

		printTotalBalance: async () => {

			const result = await Command.execute('printTotalBalance');
			if (!result.ok)
				return;

			const confirmationResult = await Message.confirm(Res.Msg.MSG_CONFIRM_CLOSE_DAY_AFTER_PRINT_BALANCE());
			if (!confirmationResult.ok)
				return;

			await doCloseDay();
		},
		closeDay: doCloseDay,
		findInvoice: async () => {
			await releaseAndLoadTicket(() => Command.execute('showInvoices'));
		},
		configurePriceList: async () => {
			await Command.execute('configurePriceList');
		},
		showDeliveryNotes: async () => {
			await releaseAndLoadTicket(() => Command.execute('showDeliveryNotes'));
		},
		showSalesOrders: async () => {
			await releaseAndLoadTicket(() => Command.execute('showSalesOrders'));
		},
		showProductsInPendingSalesOrder: async () => {
			await Command.execute('showSaleOrdersPreparations', []);
		},
		showCashControlStatus: async () => {
			await Command.execute('showCashControlStatus');
		},
		giveChangeFromCashControl: async () => {
			await Command.execute('giveChangeFromCashControl');
		},
		showSystemStatus: async () => {
			const result = await Command.execute('showSystemStatus');
			if (result.ok)
				await doCloseDay();
		},
		configureMenu: async () => {
			await Command.execute('configureMenu');
		},
		showAppointments: async () => {
			const result = await Command.execute('showAppointments');
			if (result.ok) {
				await SalesStore.loadTicketById(result.value.ticketId);
				await CustomerDisplay.displayTicketTotal(SalesStore.getTicket());
			}
		},
		printTrainingSession: async () => {
			await Command.execute('printTrainingSession');
		},
		runAdmin: async () => {
			void Command.execute('runAdmin');
		},
		resetPreparationCounter: async () => {
			await Command.execute('resetPreparationCounter');
		},
		selectOpenTicket: async () => {
			await Command.execute('selectOpenTicket');
		},
		minimize: async () => {
			await Command.execute('minimizeWindow');
		},
		doClocking: async () => {
			await Command.execute('doClocking');
		},
		showTakeOutOrders: async () => {
			await releaseAndLoadTicket(() => Command.execute('showTakeOutOrders'));
		},
		configureSmartMenuOrderComms: async () => {
			await Command.execute('configureSmartMenuOrderComms');
		},
		showDeliveryPersonStatuses: async () => {
			await Command.execute('showDeliveryPersonStatuses');
		},
		hasCashControlNotification: params.hasCashControlNotification,
		hasTakeOutOrdersNotification: params.hasTakeOutOrdersNotification,
		getIntroducedText: params.getIntroducedText,
		clearIntroducedText: params.clearIntroducedText,
		getTicket: () => SalesStore.getTicket(),
		getPreparationOrder: () => SalesStore.getPreparationOrder(),
		getSelectedLine: () => params.getSelectedLine(),
		prepareTicket: async () => {
			await Command.execute('prepareCurrentTicket');
		},
		requestCourses: async () => {
			await Command.execute('requestCurrentCourses');
		},
		closeTicket: async () => {
			if (!await updateTicketAndWarnOnPendingAmountChanged())
				return;
			await closeOrRefundCurrentTicket();
		},
		createRefund: async () => {

			// Hacemos aquí la comprobación y no en currentTicketCanBeClosedAsRefund() porque si no no podríamos hacer nunca
			// devoluciones.
			if (SalesStore.getTicket().NetAmount < 0) {
				await Message.show(Res.Msg.MSG_CANNOT_CREATE_A_REFUND_WITH_NEGATIVE_AMOUNT());
				return;
			}

			if (!await updateTicketAndWarnOnPendingAmountChanged())
				return;

			await Command.execute('closeCurrentTicket', { closeAs: 'Refund' });
		},
		printTicket: async () => {
			await Command.execute('printCurrentTicket', 'APPLY_TICKET_PRINTED_FLOW');
		},

		viewTicket: async () => {
			await Command.execute('showCurrentTicket');
		},
		moveTicket: async () => {
			if (!await updateTicketAndWarnOnPendingAmountChanged())
				return;
			const result = await Command.execute('moveTicket', SalesStore.getTicket());
			if (result.ok)
				await SalesStore.loadTicketById(result.value.Id);
		},

		changePreparationOrder: async () => {
			await Command.execute('changePreparationOrder');
		},

		prepareCurrentTicketAgain: async () => {
			await Command.execute('prepareCurrentTicketAgain');
		},

		cancelTicket: async () => {
			await Command.execute('cancelTicket', { cancel: SalesStore.cancelTicket, getTicket: SalesStore.getTicket, release: SalesStore.release });
		},

		setCustomer: async () => {

			await Command.execute('setOrRemoveCustomer', {
				customer: SalesStore.getTicket().Customer,
				setCustomer: SalesStore.setCustomer,
				requiredPermission: Permission.Ticket_SetBuyerCustomer
			});

			await params.onReloadPrices();
		},

		showCurrentCourses: async () => {
			await Command.execute('showCourses', [SalesStore.getTicket().Id]);
		},

		setOffer: async () => {
			await Command.execute('setTicketOffers');
		},

		selectTicketInLocation: async () => {
			await Command.execute('selectTicketInLocation', SalesStore.getTicket().Location);
		},

		parkTicket: async () => {
			await Command.execute('parkTicket');
		},

		showRecentSalesByCustomer: async () => {
			await Command.execute('showRecentSalesByCustomer', SalesStore.getTicket().Customer);
		},

		splitTicket: async () => {
			const ticket = SalesStore.getTicket();
			await autoPrepareIfNeeded({ ticket, onTicketPrepared: ticket => SalesStore.loadTicketById(ticket.Id) });

			// Actualizo después de lanzar el autoPrepare para no preparar cosas de más
			if (!await updateTicketAndWarnOnPendingAmountChanged())
				return;

			const result = await Command.execute('splitTicket', { ticket, addPayments: SalesStore.addPayments });
			// Si no ha funcionado la división actualizo mi ticket por si acaso y pinto el nuevo total pendiente
			if (!result.ok) {
				await SalesStore.updateTicket();
				await CustomerDisplay.displayTicketTotal(SalesStore.getTicket());
			}
		},
		setTicketFriendlyName: async () => {
			await Command.execute('setTicketFriendlyName', { setFriendlyName: SalesStore.setFriendlyName, currentFriendlyName: SalesStore.getTicket().FriendlyName });
		},
		setTableCompanions: async () => {
			await Command.execute('setTableCompanions');
		},
		createDeliveryNote: async () => {
			if (!await updateTicketAndWarnOnPendingAmountChanged())
				return;

			await Command.execute('closeCurrentTicket', { closeAs: 'DeliveryNote' });
			await params.onReloadPrices();
		},
		createSalesOrder: async () => {
			if (!await updateTicketAndWarnOnPendingAmountChanged())
				return;

			await Command.execute('closeCurrentTicket', { closeAs: 'SalesOrder' });
			await params.onReloadPrices();
		},
		setTicketPriceList: async () => {
			await Command.execute('setTicketPriceList');
		},
		setBarcode: async () => {
			await Command.execute('sellProductByManualBarcode', params.onProductSelected);
		},
		viewTicketAuditory: async () => {
			await Command.execute('viewTicketAuditory', SalesStore.getTicket().GlobalId);
		},
		setTicketDiscounts: async () => {
			await Command.execute('setTicketDiscount');
		},
		showReservation: async () => {
			await Command.execute('showCurrentReservation');
		},
		handleLoyaltyInfo: async () => {
			await Command.execute('handleLoyalty');
		},
		createLoss: async () => {
			if (!await updateTicketAndWarnOnPendingAmountChanged())
				return;
			await Command.execute('createLoss');
		},
		sellByPLU: async (): Promise<void> => {
			await Command.execute('sellByPLU', params.getIntroducedText(), params.onProductSelected);
			params.clearIntroducedText();
		},

		changePrintMode: async () => {
			const result = await Command.execute('changePrintMode');
			if (result.ok) {
				SessionProvider.setTicketWenPrint(result.value);
				params.onPrintModeChanged(result.value);
			}
		},
		changeTicketOpener: async () => {
			await Command.execute('changeTicketOpener');
		},
		getProductAvailability: async () => {
			await Command.execute('getProductAvailability');
		},
		inviteTicketLine: async () => {
			const line = params.getSelectedLine();

			if (!line)
				return;

			await Command.execute('inviteTicketLine', line);
		},
		composeUnusualProduct: async () => {
			await Command.execute('composeUnusualProduct', params.onProductSelected);
		},
		editTipSettings: async () => {
			await Command.execute('editTipSettings');
		},
		setTicketServiceCharge: async () => {
			await Command.execute('editTicketServiceCharge');
		},
		extractPendingTip: async () => {
			await Command.execute('extractPendingTip');
		},
		printLastInvoice: async () => {
			await Command.execute('printLastInvoice');
		},
		searchProductByText: async () => {
			await Command.execute('sellProductByText', params.onProductSelected);
		},
		removeTicketLine: async () => {
			const line = params.getSelectedLine();
			if (line === null)
				return;
			await Command.execute('removeTicketLine', line);
		},
		searchTakeOutOrder: async () => {
			await Command.execute('searchTakeOutOrder');
		},
		completeMenu: async () => {
			await Command.execute('completeMenu');
		},
		startTakeOutOrder: async () => {
			await Command.execute('startTakeOutOrder');
		},
		endTakeOutOrder: async () => {
			if (!await updateTicketAndWarnOnPendingAmountChanged())
				return;

			await Command.execute('endTakeOutOrder');
		},
		showTicketPayments: async () => {
			await Command.execute('showTicketPayments');
		},
		prepareTicketLine: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('prepareTicketLine', line);
		},
		prepareTicketLineAgain: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('prepareTicketLineAgain', line);
		},
		requestOrderedTicketLine: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('requestTicketLine', line);
		},
		setTicketLinePreparationOrder: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('setLinePreparationOrder', line);
		},
		setTicketLineCreator: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('setTicketLineCreator', line);
		},
		setTicketLineAddins: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('setTicketLineAddins', line);
		},
		setTicketLineDiscount: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('setTicketLineDiscount', line);
		},
		addTicketLineNote: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('addTicketLineNote', line);
		},
		editMenu: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('composeMenu', line, 'edition');
		},
		selectSaleLocation: async () => {
			// Si el usuario no tiene permisos para seleccionar mesa nunca llegaría aquí.
			// De todos modos, no podemos invocar el comando selectSaleLocation porque
			// pondría otra pantalla por encima.
			await params.applyFlowAction(FlowAction.ChangeLocation);
		},
		closeSession: async () => {
			await params.applyFlowAction(FlowAction.ChangeUser);
		},
		showProductDetails: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('showProductDetails', line.Product, SalesStore.getTicket().Location);
		},
		adjustIngredients: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('adjustTicketLineIngredients', line);
		},
		showScaleTickets: async () => {
			await Command.execute('showScaleTickets', params.onProductSelected);
		},
		setMinTicketAmount: async () => {
			await Command.execute('setMinTicketAmount');
		},
		setNextSeatNumber: async () => {
			await Command.execute('nextSeatNumber');
		},
		printLastTicketGift: async () => {
			await Command.execute('printLastTicketGift');
		},

		getSearchScreenMode: params.getSearchMode,
		toggleSearchScreenMode: params.onToggleSearchMode,
		getPrintMode: params.getPrintMode,
		editLineSeatNumber: async () => {
			const line = params.getSelectedLine();
			if (line)
				await Command.execute('editLineSeatNumber', line);
		},
		createInternalConsumption: async () => {
			if (!await updateTicketAndWarnOnPendingAmountChanged())
				return;
			await Command.execute('createInternalConsumption');
		},

		showCardProcessorAdvancedOptions: async () => {
			await Command.execute('showCardProcessorAdvancedOptions');
		}

	};
}

